import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Archive,
  BookOpen,
  Check,
  Eye,
  EyeOff,
  History,
  Inbox,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import { cn } from "../../../../utils/cn";
import { Badge } from "../../../../components/ui/Badge";
import { Button } from "../../../../components/ui/Button";
import { EmptyState } from "../../../../components/ui/EmptyState";
import { Modal } from "../../../../components/ui/Modal";
import { PendingBackendNotice } from "../../../../components/ui/PendingBackendNotice";
import { RelativeTime } from "../../../../components/ui/RelativeTime";
import { Skeleton } from "../../../../components/ui/Skeleton";
import { showToast } from "../../../../utils/showToast";
import { apiErrorMessage, apiErrorStatus } from "../../../../utils/apiErrorMessage";
import {
  createAiMemoryFact,
  deleteAiMemoryFact,
  getAiMemory,
  isEndpointMissing,
  runAiMemoryBackfill,
  updateAiMemoryFact,
} from "./aiMemoryApi";
import { AUDIENCE_FALLBACK_LABELS } from "./agentsListHelpers";
import { HANDOFF_AREA_LABELS } from "./handoffContacts";
import {
  MEMORY_AUDIENCES,
  MEMORY_BACKFILL_DAYS,
  MEMORY_CONTENT_MAX,
  MEMORY_PROMPT_MAX_CHARS,
  MEMORY_TOPIC_MAX,
  MEMORY_VIEWS,
  draftToBody,
  editChanges,
  normalizeBackfillResponse,
  normalizeMemoryResponse,
  resolveSuperseded,
  validateFactDraft,
} from "./aiMemory";
import {
  CharCounter,
  ChipRadioGroup,
  CONTROL_CLS,
  MetaBadge,
  PRIMARY_BTN_CLS,
  SECONDARY_BTN_CLS,
  SectionTitle,
  TEXTAREA_CLS,
} from "./agentSections/primitives";

// Memoria aprendida del asistente: datos generales (horarios, procedimientos,
// facturación) que la IA usa al responder, aprendidos de las respuestas del
// equipo a consultas internas o agregados a mano. Son por courier.
//
// La página es el único lugar donde un admin los revisa: aprobar/rechazar los
// pendientes, editar, desactivar/activar, borrar y lanzar el aprendizaje de
// consultas pasadas. `canManage=false` la deja en solo lectura sin esconder el
// contenido: el rol puede ver la memoria, pero no cambiarla.

// Relectura única tras lanzar el aprendizaje de consultas pasadas: el backend
// extrae en segundo plano (una llamada al modelo por consulta).
const BACKFILL_REFRESH_MS = 30000;

// Botón de acción del ítem: alto táctil en móvil (h-11) y compacto desde md.
const ITEM_BTN_CLS = "h-11 md:h-8";

const EMPTY_STATES = {
  pending: {
    Icon: Inbox,
    title: "No hay datos por aprobar",
    description:
      "Cuando tu equipo responda una consulta de la IA, el dato que se pueda reutilizar aparece acá para que lo revises.",
  },
  active: {
    Icon: BookOpen,
    title: "Todavía no hay datos activos",
    description:
      "Aprueba los propuestos o agrega uno a mano. La IA los usa con todos los contactos de la audiencia elegida.",
  },
  disabled: {
    Icon: Archive,
    title: "No hay datos desactivados",
    description: "Los datos que desactives quedan acá y puedes volver a activarlos.",
  },
};

/**
 * Texto del error de una acción, de más específico a más genérico. Un 404/405
 * sin texto útil es la ruta aún no desplegada; un 4xx con mensaje del backend
 * explica mejor que el genérico; un 5xx queda fuera (su texto es interno).
 * @param {unknown} err
 * @param {(key: string) => string} t
 * @param {string} fallback clave i18n de la acción que falló
 * @returns {string}
 */
function actionErrorMessage(err, t, fallback) {
  const status = apiErrorStatus(err);
  const serverMessage = status !== null && status < 500 ? apiErrorMessage(err, "") : "";
  if (isEndpointMissing(err) && !serverMessage) {
    return t("La memoria aprendida todavía no está disponible.");
  }
  if (serverMessage) return serverMessage;
  return t(fallback);
}

function MemorySkeleton() {
  return (
    <div
      className="divide-y divide-border rounded-lg border border-border bg-bg"
      aria-hidden="true"
    >
      {[0, 1, 2].map((i) => (
        <div key={i} className="p-4 space-y-2">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}

/**
 * Formulario de alta y edición de un dato. Vive a nivel de módulo (no dentro
 * del render): definirlo adentro remontaría el subtree en cada tecla y mataría
 * el foco.
 *
 * @param {object} props
 * @param {object|null} props.initial - fact en edición; null para alta
 * @param {(id: string) => string} props.audienceLabel
 * @param {(draft: object) => Promise<void>} props.onSubmit - maneja sus propios
 *   errores (toast); acá solo se evita el doble envío
 * @param {() => void} props.onCancel
 * @param {string} props.submitLabel
 */
function FactForm({ initial, audienceLabel, onSubmit, onCancel, submitLabel }) {
  const { t } = useTranslation();
  // Ids únicos por instancia: puede haber un formulario de alta y uno de
  // edición montados a la vez.
  const uid = useId();
  const topicId = `memory-topic-${uid}`;
  const contentId = `memory-content-${uid}`;
  const audiencesId = `memory-audiences-${uid}`;

  const [topic, setTopic] = useState(initial?.topic || "");
  const [content, setContent] = useState(initial?.content || "");
  // El alta parte de "todas las audiencias": es el caso más común y evita un
  // error de validación que el usuario no espera. Al editar se respeta lo que
  // ya tenía (aunque venga vacío): rellenarlo con "all" ensancharía el alcance
  // del dato sin que nadie lo haya pedido.
  const [audiences, setAudiences] = useState(() => {
    if (!initial) return ["all"];
    return Array.isArray(initial.audiences) ? initial.audiences : [];
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Abrir el formulario (alta o edición) lleva el foco al primer campo: el
  // botón que lo abrió desaparece (edición) o queda lejos (alta), y sin esto el
  // foco cae al <body>.
  const topicRef = useRef(null);
  useEffect(() => {
    topicRef.current?.focus();
  }, []);

  const toggleAudience = (id) => {
    setAudiences((prev) => {
      if (prev.includes(id)) return prev.filter((value) => value !== id);
      // "Todas" es exclusiva: marcar otra audiencia la desmarca, y viceversa.
      return id === "all" ? ["all"] : [...prev.filter((value) => value !== "all"), id];
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const draft = { topic, content, audiences };
    const nextErrors = validateFactDraft(draft);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSaving(true);
    try {
      await onSubmit(draft);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="space-y-3" onSubmit={handleSubmit} noValidate>
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <label htmlFor={topicId} className="hd-field-label block text-xs font-medium text-fg">
            {t("Tema")}
          </label>
          <CharCounter value={topic.length} max={MEMORY_TOPIC_MAX} />
        </div>
        <input
          ref={topicRef}
          id={topicId}
          type="text"
          value={topic}
          maxLength={MEMORY_TOPIC_MAX}
          onChange={(event) => setTopic(event.target.value)}
          aria-invalid={!!errors.topic}
          aria-describedby={errors.topic ? `${topicId}-error` : undefined}
          // Mismo criterio que ui/Input: el campo inválido se marca en rojo.
          className={cn(CONTROL_CLS, errors.topic && "!border-status-error")}
        />
        {errors.topic && (
          <p
            id={`${topicId}-error`}
            className="text-[12px] md:text-[11px] text-status-error"
            role="alert"
          >
            {t(errors.topic)}
          </p>
        )}
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <label htmlFor={contentId} className="hd-field-label block text-xs font-medium text-fg">
            {t("Dato")}
          </label>
          <CharCounter value={content.length} max={MEMORY_CONTENT_MAX} />
        </div>
        <textarea
          id={contentId}
          rows={3}
          value={content}
          maxLength={MEMORY_CONTENT_MAX}
          onChange={(event) => setContent(event.target.value)}
          aria-invalid={!!errors.content}
          aria-describedby={errors.content ? `${contentId}-error` : undefined}
          className={cn(TEXTAREA_CLS, errors.content && "!border-status-error")}
        />
        {errors.content ? (
          <p
            id={`${contentId}-error`}
            className="text-[12px] md:text-[11px] text-status-error"
            role="alert"
          >
            {t(errors.content)}
          </p>
        ) : (
          <p className="text-[12px] md:text-[11px] text-fg-muted">
            {t(
              "Escribe un dato general y estable (horarios, procedimientos, datos de facturación). Nada de un caso puntual ni teléfonos de personas.",
            )}
          </p>
        )}
      </div>

      <fieldset
        className="space-y-2"
        aria-describedby={errors.audiences ? `${audiencesId}-error` : undefined}
      >
        <legend className="hd-field-label block text-xs font-medium text-fg">
          {t("Audiencias")}
        </legend>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {MEMORY_AUDIENCES.map((id) => (
            <label key={id} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={audiences.includes(id)}
                onChange={() => toggleAudience(id)}
                className="!w-5 !h-5 md:!w-4 md:!h-4 accent-brand-500 shrink-0"
              />
              <span className="text-xs text-fg">{audienceLabel(id)}</span>
            </label>
          ))}
        </div>
        {errors.audiences && (
          <p
            id={`${audiencesId}-error`}
            className="text-[12px] md:text-[11px] text-status-error"
            role="alert"
          >
            {t(errors.audiences)}
          </p>
        )}
      </fieldset>

      <div className="flex flex-wrap gap-2">
        <button type="submit" className={cn(PRIMARY_BTN_CLS, "w-full sm:w-auto")} disabled={saving}>
          {submitLabel}
        </button>
        <button
          type="button"
          className={cn(SECONDARY_BTN_CLS, "w-full sm:w-auto")}
          onClick={onCancel}
          disabled={saving}
        >
          {t("Cancelar")}
        </button>
      </div>
    </form>
  );
}

/**
 * Un dato de la lista. Componente de módulo (ver FactForm).
 */
function FactItem({
  fact,
  view,
  canManage,
  busy,
  busyAction,
  editing,
  audienceLabel,
  onEdit,
  onCancelEdit,
  onSubmitEdit,
  onApprove,
  onReject,
  onDisable,
  onEnable,
  onRequestDelete,
}) {
  const { t } = useTranslation();

  if (editing) {
    return (
      <li className="p-4 space-y-2">
        <FactForm
          initial={fact}
          audienceLabel={audienceLabel}
          onSubmit={onSubmitEdit}
          onCancel={onCancelEdit}
          submitLabel={t("Guardar cambios")}
        />
      </li>
    );
  }

  const source = fact.source || {};
  const superseded = resolveSuperseded(fact);

  // Origen en una línea. Cada parte es opcional: un dato manual no tiene
  // consulta, y un backend viejo puede no mandar área ni quién respondió.
  const originParts = [];
  if (source.kind === "manual") {
    originParts.push({ key: "manual", node: <span>{t("Agregado a mano")}</span> });
  } else if (source.kind === "team_consult") {
    originParts.push({
      key: "consult",
      node: (
        <span>
          {t("Consulta")}
          {source.consult_code && (
            <>
              {" "}
              <span className="font-mono">#{source.consult_code}</span>
            </>
          )}
        </span>
      ),
    });
    if (source.area) {
      originParts.push({
        key: "area",
        // Las áreas de la consulta son las mismas de los avisos de derivación
        // (primera_milla, ultima_milla, finanzas); una desconocida va cruda.
        node: (
          <span>
            {/* Reemplazo por función: un texto con "$&" o "$'" no se interpreta
                como patrón de String.replace. */}
            {t("Área: {{area}}").replace("{{area}}", () =>
              HANDOFF_AREA_LABELS[source.area] ? t(HANDOFF_AREA_LABELS[source.area]) : source.area,
            )}
          </span>
        ),
      });
    }
    if (source.answered_by) {
      originParts.push({
        key: "answeredBy",
        node: <span>{t("Respondió {{name}}").replace("{{name}}", () => source.answered_by)}</span>,
      });
    }
    const when = source.answered_at || fact.created_at;
    if (when) originParts.push({ key: "when", node: <RelativeTime value={when} t={t} /> });
  }

  return (
    <li className="p-4 space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-sm font-medium text-fg break-words">{fact.topic}</h3>
        <div className="flex flex-wrap gap-1">
          {fact.audiences.map((id) => (
            <Badge key={id} tone="neutral">
              {audienceLabel(id)}
            </Badge>
          ))}
          {fact.edited && <MetaBadge>{t("Editado")}</MetaBadge>}
        </div>
      </div>

      <p className="text-sm text-fg whitespace-pre-wrap break-words">{fact.content}</p>

      {superseded && (
        <div className="rounded-md bg-bg-muted px-3 py-2 text-xs">
          <span className="font-medium text-fg-muted">{t("Reemplaza a:")}</span>{" "}
          <span className="font-medium text-fg break-words">{superseded.topic}</span>
          <p className="mt-0.5 text-fg-muted whitespace-pre-wrap break-words">
            {superseded.content}
          </p>
        </div>
      )}
      {!superseded && fact.supersedes_id && (
        <p className="text-xs text-fg-muted">
          {t("Reemplaza un dato anterior que ya no está disponible.")}
        </p>
      )}

      {originParts.length > 0 && (
        <p className="text-[12px] md:text-[11px] text-fg-muted flex flex-wrap gap-x-2 gap-y-0.5">
          {originParts.map((part, index) => (
            <React.Fragment key={part.key}>
              {index > 0 && <span aria-hidden="true"> · </span>}
              {part.node}
            </React.Fragment>
          ))}
        </p>
      )}

      {view === "pending" && (source.question || source.answer) && (
        <details className="pt-1">
          <summary className="cursor-pointer text-xs font-medium text-brand-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded">
            {t("Ver consulta y respuesta")}
          </summary>
          <div className="mt-2 space-y-2">
            {source.question && (
              <div className="space-y-1">
                <p className="text-[12px] md:text-[11px] font-medium text-fg-muted">
                  {t("Pregunta de la IA")}
                </p>
                <p className="whitespace-pre-wrap text-xs text-fg bg-bg-muted rounded-md px-3 py-2">
                  {source.question}
                </p>
              </div>
            )}
            {source.answer && (
              <div className="space-y-1">
                <p className="text-[12px] md:text-[11px] font-medium text-fg-muted">
                  {t("Respuesta del equipo")}
                </p>
                <p className="whitespace-pre-wrap text-xs text-fg bg-bg-muted rounded-md px-3 py-2">
                  {source.answer}
                </p>
              </div>
            )}
          </div>
        </details>
      )}

      {canManage && (
        <div className="flex flex-wrap gap-2 pt-1">
          {view === "pending" && (
            <>
              <Button
                size="sm"
                variant="primary"
                className={ITEM_BTN_CLS}
                iconLeft={<Check className="w-3.5 h-3.5" aria-hidden="true" />}
                onClick={() => onApprove(fact)}
                disabled={busy}
                loading={busy && busyAction === "approve"}
              >
                {t("Aprobar")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className={ITEM_BTN_CLS}
                iconLeft={<Pencil className="w-3.5 h-3.5" aria-hidden="true" />}
                onClick={() => onEdit(fact.id)}
                disabled={busy}
              >
                {t("Editar")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className={ITEM_BTN_CLS}
                iconLeft={<X className="w-3.5 h-3.5" aria-hidden="true" />}
                onClick={() => onReject(fact)}
                disabled={busy}
                loading={busy && busyAction === "reject"}
              >
                {t("Rechazar")}
              </Button>
            </>
          )}

          {view === "active" && (
            <>
              <Button
                size="sm"
                variant="outline"
                className={ITEM_BTN_CLS}
                iconLeft={<Pencil className="w-3.5 h-3.5" aria-hidden="true" />}
                onClick={() => onEdit(fact.id)}
                disabled={busy}
              >
                {t("Editar")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className={ITEM_BTN_CLS}
                iconLeft={<EyeOff className="w-3.5 h-3.5" aria-hidden="true" />}
                onClick={() => onDisable(fact)}
                disabled={busy}
                loading={busy && busyAction === "disable"}
              >
                {t("Desactivar")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className={cn(ITEM_BTN_CLS, "text-status-error")}
                iconLeft={<Trash2 className="w-3.5 h-3.5" aria-hidden="true" />}
                onClick={() => onRequestDelete(fact)}
                disabled={busy}
              >
                {t("Borrar")}
              </Button>
            </>
          )}

          {view === "disabled" && (
            <>
              <Button
                size="sm"
                variant="outline"
                className={ITEM_BTN_CLS}
                iconLeft={<Eye className="w-3.5 h-3.5" aria-hidden="true" />}
                onClick={() => onEnable(fact)}
                disabled={busy}
                loading={busy && busyAction === "enable"}
              >
                {t("Activar")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className={ITEM_BTN_CLS}
                iconLeft={<Pencil className="w-3.5 h-3.5" aria-hidden="true" />}
                onClick={() => onEdit(fact.id)}
                disabled={busy}
              >
                {t("Editar")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className={cn(ITEM_BTN_CLS, "text-status-error")}
                iconLeft={<Trash2 className="w-3.5 h-3.5" aria-hidden="true" />}
                onClick={() => onRequestDelete(fact)}
                disabled={busy}
              >
                {t("Borrar")}
              </Button>
            </>
          )}
        </div>
      )}
    </li>
  );
}

export default function AiMemoryTab({ catalog, canManage = true }) {
  const { t, i18n } = useTranslation();

  // loading → consultando; ready → hay respuesta (aunque venga vacía);
  // missing → la ruta no está desplegada (404/405); error → falló de verdad.
  const [view, setView] = useState(MEMORY_VIEWS[0]);
  const [state, setState] = useState("loading");
  const [facts, setFacts] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, active: 0, disabled: 0 });
  const [activeChars, setActiveChars] = useState(null);
  const [maxChars, setMaxChars] = useState(MEMORY_PROMPT_MAX_CHARS);
  const [truncated, setTruncated] = useState(false);
  // Acciones en vuelo por dato ({ [id]: action }): un mapa y no un solo slot,
  // para que lanzar una acción en otro ítem no rehabilite los botones del
  // primero mientras su request sigue en vuelo (doble envío → 409).
  const [busy, setBusy] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [error, setError] = useState(null);

  // Descarta respuestas de una vista vieja: cambiar de pestaña mientras la
  // consulta anterior sigue en vuelo no puede pintar la lista equivocada.
  const requestIdRef = useRef(0);
  // StrictMode monta → limpia → monta sobre el MISMO fiber: el flag se re-fija
  // dentro del efecto, no solo al declarar el ref.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // `t` cambia de identidad al cambiar el idioma: si entrara a las deps del
  // load, traducir la pantalla volvería a consultar el endpoint sin que cambie
  // la vista. Los textos de error usan la instancia vigente al momento del fallo.
  const tRef = useRef(t);
  tRef.current = t;

  // Etiqueta de una audiencia: catálogo primero, copia local después y el id
  // crudo como último recurso. "all" no viene en el catálogo de agentes.
  const audienceLabel = useCallback(
    (id) => {
      if (id === "all") return t("Todas las audiencias");
      const fromCatalog = (catalog?.audiences || []).find((a) => a?.id === id);
      if (fromCatalog?.label) return fromCatalog.label;
      return AUDIENCE_FALLBACK_LABELS[id] ? t(AUDIENCE_FALLBACK_LABELS[id]) : id;
    },
    // Con `t` en las deps: cambiar de idioma re-rotula las audiencias.
    [catalog, t],
  );

  const load = useCallback(
    async ({ silent = false } = {}) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      const isCurrent = () => aliveRef.current && requestIdRef.current === requestId;

      // Silenciosa = recarga tras una acción: la lista no vuelve al esqueleto.
      if (!silent) setState("loading");
      setError(null);

      try {
        const res = await getAiMemory({ status: view });
        if (!isCurrent()) return;
        const data = normalizeMemoryResponse(res);

        setFacts(data.facts);
        setCounts(data.counts);
        setActiveChars(data.activeChars);
        setMaxChars(data.maxChars);
        setTruncated(data.truncated);
        setState("ready");
      } catch (err) {
        if (!isCurrent()) return;
        // Una recarga silenciosa fallida no debe borrar lo que ya se mostraba.
        if (silent) return;
        if (isEndpointMissing(err)) {
          setState("missing");
        } else {
          // Mismo criterio que las acciones: un 4xx con texto del backend se
          // muestra; un 5xx o un fallo de red, el genérico (su texto es interno).
          const status = apiErrorStatus(err);
          const serverMessage = status !== null && status < 500 ? apiErrorMessage(err, "") : "";
          setError(serverMessage || tRef.current("No se pudo cargar la memoria aprendida."));
          setState("error");
        }
      }
    },
    [view],
  );

  useEffect(() => {
    load();
  }, [load]);

  // Toda recarga posterior a una acción (y la diferida del backfill) llama a la
  // `load` VIGENTE: si el usuario cambió de vista mientras la acción volaba, una
  // closure vieja pediría la vista anterior con un requestId más nuevo y
  // pintaría esos datos bajo la pestaña actual.
  const loadRef = useRef(load);
  loadRef.current = load;
  const viewRef = useRef(view);
  viewRef.current = view;
  const reloadSilently = useCallback(() => loadRef.current({ silent: true }), []);

  const markBusy = useCallback((id, action) => {
    setBusy((prev) => ({ ...prev, [id]: action }));
  }, []);
  const clearBusy = useCallback((id) => {
    setBusy((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);
  const backfillTimerRef = useRef(null);
  useEffect(() => () => window.clearTimeout(backfillTimerRef.current), []);

  // Errores de acciones: toast con el texto más específico disponible y
  // recarga silenciosa cuando la lista quedó vieja: un 404 CON mensaje (otro
  // admin borró el dato) o un 409 (otro admin ya lo revisó y la transición ya
  // no aplica).
  const reportActionError = useCallback(
    async (err, fallback) => {
      showToast(actionErrorMessage(err, tRef.current, fallback), "error");
      const status = apiErrorStatus(err);
      if (status === 409 || (status === 404 && apiErrorMessage(err, ""))) {
        await reloadSilently();
      }
    },
    [reloadSilently],
  );

  // Acción de revisión (approve/reject/disable/enable). Bloquea SOLO el ítem y
  // recarga la lista, que el backend reordena.
  const runAction = useCallback(
    async (fact, action, messages) => {
      markBusy(fact.id, action);
      try {
        await updateAiMemoryFact(fact.id, { action });
        showToast(tRef.current(messages.success), "success");
        await reloadSilently();
      } catch (err) {
        await reportActionError(err, messages.failure);
      } finally {
        clearBusy(fact.id);
      }
    },
    [clearBusy, markBusy, reloadSilently, reportActionError],
  );

  const handleApprove = useCallback(
    (fact) =>
      runAction(fact, "approve", {
        success: "Dato aprobado: la IA ya lo usa.",
        failure: "No se pudo aprobar el dato.",
      }),
    [runAction],
  );

  const handleReject = useCallback(
    (fact) =>
      runAction(fact, "reject", {
        success: "Dato rechazado.",
        failure: "No se pudo rechazar el dato.",
      }),
    [runAction],
  );

  const handleDisable = useCallback(
    (fact) =>
      runAction(fact, "disable", {
        success: "Dato desactivado: la IA deja de usarlo.",
        failure: "No se pudo desactivar el dato.",
      }),
    [runAction],
  );

  const handleEnable = useCallback(
    (fact) =>
      runAction(fact, "enable", {
        success: "Dato activado.",
        failure: "No se pudo activar el dato.",
      }),
    [runAction],
  );

  const handleCreate = useCallback(
    async (draft) => {
      try {
        await createAiMemoryFact(draftToBody(draft));
        showToast(tRef.current("Dato agregado: la IA ya lo usa."), "success");
        setAdding(false);
        // El alta queda active: si no estamos en esa vista, cambiar a ella
        // dispara la carga por el efecto; si ya estamos, se recarga.
        if (viewRef.current !== "active") setView("active");
        else await reloadSilently();
      } catch (err) {
        await reportActionError(err, "No se pudo guardar el dato.");
      }
    },
    [reloadSilently, reportActionError],
  );

  const handleSubmitEdit = useCallback(
    async (draft) => {
      const original = facts.find((fact) => fact.id === editingId);
      // Solo viaja lo que cambió: el backend marca `edited` y, en un dato
      // activo, recalcula qué reemplaza; reenviar lo intacto lo dispararía igual.
      const body = editChanges(original, draftToBody(draft));
      if (Object.keys(body).length === 0) {
        setEditingId(null);
        return;
      }
      try {
        await updateAiMemoryFact(editingId, body);
        setEditingId(null);
        showToast(tRef.current("Dato actualizado."), "success");
        await reloadSilently();
      } catch (err) {
        // El formulario queda abierto para corregir lo que el backend rechazó.
        await reportActionError(err, "No se pudo guardar el dato.");
      }
    },
    [editingId, facts, reloadSilently, reportActionError],
  );

  const handleConfirmDelete = useCallback(async () => {
    const target = pendingDelete;
    setPendingDelete(null);
    if (!target) return;

    markBusy(target.id, "delete");
    try {
      await deleteAiMemoryFact(target.id);
      showToast(tRef.current("Dato borrado."), "success");
      await reloadSilently();
    } catch (err) {
      await reportActionError(err, "No se pudo borrar el dato.");
    } finally {
      clearBusy(target.id);
    }
  }, [clearBusy, markBusy, pendingDelete, reloadSilently, reportActionError]);

  const handleBackfill = useCallback(async () => {
    setBackfilling(true);
    try {
      const res = await runAiMemoryBackfill({ days: MEMORY_BACKFILL_DAYS });
      const { started, candidates, skippedNoAgent } = normalizeBackfillResponse(res);
      // Las consultas de agentes borrados o sin proveedor no se pueden revisar
      // (el extractor usa el modelo del agente que consultó).
      const toReview = Math.max(0, candidates - skippedNoAgent);
      if (!started) {
        showToast(
          tRef.current(
            "Ya hay un aprendizaje en curso. Lo que se pueda reutilizar aparecerá en Por aprobar.",
          ),
          "warning",
        );
      } else if (candidates > 0 && toReview === 0) {
        showToast(
          tRef.current(
            "Las consultas del período son de agentes que ya no existen o no tienen proveedor: no hay nada que revisar.",
          ),
          "info",
        );
      } else if (toReview === 0) {
        showToast(
          tRef.current("No hay consultas nuevas para revisar en los últimos 30 días."),
          "info",
        );
      } else {
        showToast(
          tRef
            .current(
              "Revisando {{n}} consultas en segundo plano. Lo que se pueda reutilizar aparecerá en Por aprobar.",
            )
            .replace("{{n}}", () => String(toReview)),
          "info",
        );
        // El backend extrae en segundo plano: se lleva al admin a la vista
        // donde aparecerán y se relee una vez más tarde, sin sondear.
        if (viewRef.current !== "pending") setView("pending");
        else await reloadSilently();
        window.clearTimeout(backfillTimerRef.current);
        backfillTimerRef.current = window.setTimeout(() => {
          loadRef.current({ silent: true });
        }, BACKFILL_REFRESH_MS);
      }
    } catch (err) {
      await reportActionError(err, "No se pudo aprender de las consultas pasadas.");
    } finally {
      setBackfilling(false);
    }
  }, [reloadSilently, reportActionError]);

  const handleViewChange = useCallback((next) => {
    // Los formularios abiertos pertenecen a la vista anterior: cambiar de
    // pestaña no puede dejar un editor huérfano.
    setEditingId(null);
    setAdding(false);
    setView(next);
  }, []);

  // Etiqueta + contador en una sola línea: en 390px un chip partido en tres
  // renglones se lee como tres controles. Si no caben, el grupo se desliza en
  // horizontal, igual que la fila de pestañas del kit (ui/Tabs) en móvil.
  const viewOptions = [
    { value: "pending", text: `${t("Por aprobar")} · ${counts.pending}` },
    { value: "active", text: `${t("Activos")} · ${counts.active}` },
    { value: "disabled", text: `${t("Desactivados")} · ${counts.disabled}` },
  ].map(({ value, text }) => ({
    value,
    label: <span className="whitespace-nowrap">{text}</span>,
  }));

  const empty = EMPTY_STATES[view] || EMPTY_STATES.pending;

  return (
    <section className="space-y-4" aria-label={t("Memoria aprendida")}>
      <p className="text-xs text-fg-muted">
        {t(
          "Datos que la IA usa al responder, confirmados por tu equipo. Se aprenden de las respuestas a las consultas internas o los agregas tú.",
        )}
      </p>

      {/* Anuncio contextual para lectores de pantalla: un número suelto no dice
          de qué es. Vacío hasta que hay respuesta, para no anunciar un 0 que el
          backend todavía no dijo. */}
      <p role="status" aria-atomic="true" className="sr-only">
        {state === "ready"
          ? t("{{count}} datos por aprobar").replace("{{count}}", String(counts.pending))
          : ""}
      </p>

      {/* Sin la ruta desplegada no hay nada que filtrar ni que agregar:
          ofrecer controles que solo pueden volver a fallar es ruido. */}
      {state !== "missing" && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="max-w-full overflow-x-auto">
            <ChipRadioGroup
              value={view}
              options={viewOptions}
              onChange={handleViewChange}
              ariaLabel={t("Estado de los datos")}
            />
          </div>

          {canManage && (
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setAdding((prev) => !prev)}
                aria-expanded={adding}
                className={cn(PRIMARY_BTN_CLS, "w-full sm:w-auto")}
              >
                <Plus className="w-3.5 h-3.5" aria-hidden="true" />
                {t("Agregar dato")}
              </button>
              <button
                type="button"
                onClick={handleBackfill}
                disabled={backfilling}
                className={cn(SECONDARY_BTN_CLS, "w-full sm:w-auto")}
              >
                <History className="w-3.5 h-3.5" aria-hidden="true" />
                {backfilling ? t("Aprendiendo…") : t("Aprender de consultas pasadas (30 días)")}
              </button>
            </div>
          )}
        </div>
      )}

      {!canManage && state !== "missing" && (
        <p className="text-[12px] md:text-[11px] text-fg-muted">
          {t("Tu rol puede ver la memoria, pero solo un administrador puede cambiarla.")}
        </p>
      )}

      {view === "active" && typeof activeChars === "number" && (
        <p className="text-[12px] md:text-[11px] text-fg-muted">
          {t("{{n}} de {{max}} caracteres en uso")
            .replace("{{n}}", activeChars.toLocaleString(i18n?.language))
            .replace("{{max}}", maxChars.toLocaleString(i18n?.language))}
        </p>
      )}

      {truncated && (
        <div
          role="note"
          className="flex items-start gap-2 rounded-lg border border-border bg-status-warning-soft px-3 py-2 text-xs text-fg"
        >
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          <span>
            {t(
              "La memoria activa supera el tope de caracteres: la IA usa los datos más recientes. Desactiva los que ya no sirvan.",
            )}
          </span>
        </div>
      )}

      {adding && state !== "missing" && (
        <div className="rounded-lg border border-border bg-bg p-4 space-y-3">
          <SectionTitle>{t("Nuevo dato")}</SectionTitle>
          <FactForm
            initial={null}
            audienceLabel={audienceLabel}
            onSubmit={handleCreate}
            onCancel={() => setAdding(false)}
            submitLabel={t("Guardar dato")}
          />
        </div>
      )}

      {state === "loading" && <MemorySkeleton />}

      {state === "missing" && (
        <PendingBackendNotice>
          {t("La memoria aprendida todavía no está disponible.")}
        </PendingBackendNotice>
      )}

      {state === "error" && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2"
          role="alert"
        >
          <span className="flex items-center gap-1.5 text-xs text-status-error min-w-0">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            {error}
          </span>
          <button type="button" onClick={() => load()} className={SECONDARY_BTN_CLS}>
            <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
            {t("Reintentar")}
          </button>
        </div>
      )}

      {state === "ready" && facts.length === 0 && (
        <EmptyState
          icon={<empty.Icon className="w-6 h-6" aria-hidden="true" />}
          title={t(empty.title)}
          description={t(empty.description)}
        />
      )}

      {state === "ready" && facts.length > 0 && (
        <ul className="divide-y divide-border rounded-lg border border-border bg-bg">
          {facts.map((fact) => (
            <FactItem
              key={fact.id}
              fact={fact}
              view={view}
              canManage={canManage}
              busy={Boolean(busy[fact.id])}
              busyAction={busy[fact.id] || ""}
              editing={editingId === fact.id}
              audienceLabel={audienceLabel}
              onEdit={setEditingId}
              onCancelEdit={() => setEditingId(null)}
              onSubmitEdit={handleSubmitEdit}
              onApprove={handleApprove}
              onReject={handleReject}
              onDisable={handleDisable}
              onEnable={handleEnable}
              onRequestDelete={setPendingDelete}
            />
          ))}
        </ul>
      )}

      <Modal
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title={t("Borrar dato")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>
              {t("Cancelar")}
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete}>
              {t("Borrar")}
            </Button>
          </>
        }
      >
        <p className="text-sm text-fg">
          {t("Vas a borrar este dato:")} <span className="font-medium">{pendingDelete?.topic}</span>
        </p>
        <p className="mt-2 text-sm text-fg-muted">
          {t(
            "La IA dejará de usarlo y no se puede deshacer. Si solo quieres pausarlo, desactívalo.",
          )}
        </p>
      </Modal>
    </section>
  );
}
